import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, useReducedMotion, type Variants } from "framer-motion";
import shortid from "shortid";
import {
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  Eye,
  EyeOff,
  Layers,
  Lock,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Users,
} from "lucide-react";
import { signupFree } from "../../../api/General/PlatformTerms";
import { getCountries } from "../../../api/General/Country";
import { track, AnalyticsEvents } from "../../../utils/analytics";
import { CountryView, Error as ApiError } from "../../../models/general";
import { validateEmail } from "../../../utils";
import { getApiErrorMessage } from "../../../utils/apiErrors";
import { isLikelyWorkEmail, SIGNUP_CONTACT_URL } from "../../../utils/workEmail";
import { cn } from "../../../utils/cn";

import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";
import { Checkbox } from "../../../components/ui/Checkbox";
import TermsDialog from "../../../components/terms/TermsDialog";

import Logo from "../../../assets/images/logo.png";
import CircleCheck from "../../../assets/images/circleCheck.png";

/**
 * SignupFree — public, anonymous on-ramp that creates a Free-tier company.
 *
 * Brand surface (per PRODUCT.md register=brand): cobalt + turquoise mesh,
 * inline brand mark + wordmark, gradient-border form card, Framer-Motion
 * staggered entrance, ambient sheen + progress shimmer. All ambient motion
 * collapses under prefers-reduced-motion (see src/index.css `.mz-*` rules).
 *
 * 🔴 CO-227 — NO TRIAL COPY ON THIS PAGE. Trial is retired; the reassurance line under the CTA
 * promises the Free tier and points at the paid plans, and there is no day-count to keep in sync
 * with anything. Do NOT reintroduce a trial-length literal here.
 */

function generatePassword(): string {
  return `${shortid.generate()}@Abc123`;
}

interface FormState {
  companyName: string;
  firstName: string;
  lastName: string;
  email: string;
  countryId: number | "";
  phoneNumber: string;
  password: string;
}

/** Server-rendered Razor login page. */
const LOGIN_URL = "/Account/Login";

/**
 * CO-01 — a signup the server refused on the work-email policy. Held against the exact address that
 * was refused, so the message disappears the instant the visitor edits the field instead of lingering
 * until the next submit. `contactUrl` comes back on the response so the marketing page can move
 * without a client release.
 */
interface EmailRefusal {
  email: string;
  code: "WORK_EMAIL_REQUIRED" | "INVALID_EMAIL";
  contactUrl: string;
}

const EASE = [0.22, 1, 0.36, 1] as const;

const SignupFree: React.FC = () => {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();

  const goToLogin = () => {
    if (typeof window !== "undefined") window.location.assign(LOGIN_URL);
  };

  const [form, setForm] = useState<FormState>(() => ({
    companyName: "",
    firstName: "",
    lastName: "",
    email: "",
    countryId: "",
    phoneNumber: "",
    password: generatePassword(),
  }));
  const [countries, setCountries] = useState<CountryView[]>([]);
  const [accepted, setAccepted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [touched, setTouched] = useState(false);
  const [refusal, setRefusal] = useState<EmailRefusal | null>(null);

  const timeZone = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    } catch {
      return "UTC";
    }
  }, []);

  const referenceSlug = useMemo(() => {
    if (typeof window === "undefined") return null;
    const raw = new URLSearchParams(window.location.search).get("ref");
    if (!raw) return null;
    const trimmed = raw.trim();
    return trimmed.length === 0 ? null : trimmed.slice(0, 64);
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await getCountries();
        if (!alive) return;
        if (!res.errors || res.errors.length === 0) setCountries(res.payload || []);
      } catch {
        /* surface inline only on submit */
      }
    })();
    return () => { alive = false; };
  }, []);

  const redirectTimer = useRef<number | null>(null);
  useEffect(() => {
    if (submitted) {
      redirectTimer.current = window.setTimeout(() => goToLogin(), 5000);
    }
    return () => {
      if (redirectTimer.current) window.clearTimeout(redirectTimer.current);
    };
  }, [submitted]);

  /**
   * The server's refusal, but only while it still describes what is in the field. Editing the
   * address clears it without a round trip.
   */
  const activeRefusal = useMemo(() => {
    if (!refusal) return null;
    return refusal.email === form.email.trim().toLowerCase() ? refusal : null;
  }, [refusal, form.email]);

  /**
   * CO-01. `isLikelyWorkEmail` runs the same deny-list as the server so the visitor finds out while
   * typing, but the server's verdict outranks it: the two lists can legitimately differ (the server
   * one is editable at runtime), and a direct POST skips this check entirely.
   */
  /**
   * ⚠️ THE LOCAL LIST HINTS, THE SERVER DECIDES.
   * `isLikelyWorkEmail` used to fold straight into `fieldErrors.email`, which feeds `isValid`, which
   * gates `submit()` — so a local match blocked the request before it was ever sent and the server's
   * RUNTIME-CONFIGURABLE `SignupPolicy:BlockedEmailDomainsCsv` was never consulted. Removing a domain
   * from that setting to unblock a real customer (plenty of GCC businesses genuinely run on Zoho) then
   * required a FRONTEND RELEASE. This file's own header states the contract — "always let a
   * WORK_EMAIL_REQUIRED response win over a local true" — and it was only implemented in one
   * direction. Now the local list warns and offers the contact-us escape hatch; only the server's
   * refusal actually blocks.
   */
  const localWorkEmailHint =
    form.email.trim().length > 0 &&
    validateEmail(form.email) &&
    !isLikelyWorkEmail(form.email);

  const serverWorkEmailRefusal = activeRefusal?.code === "WORK_EMAIL_REQUIRED";

  /** Drives the inline warning + contact-us link only. NOT a submit gate — see above. */
  const emailNeedsWorkAddress = serverWorkEmailRefusal || localWorkEmailHint;

  const fieldErrors = useMemo(() => {
    const e: Partial<Record<keyof FormState | "terms", string>> = {};
    if (!form.companyName.trim()) e.companyName = t("Required");
    if (!form.firstName.trim()) e.firstName = t("Required");
    if (!form.lastName.trim()) e.lastName = t("Required");
    if (!form.email.trim()) e.email = t("Required");
    else if (!validateEmail(form.email) || activeRefusal?.code === "INVALID_EMAIL")
      e.email = t("Please enter a valid email.");
    // Only the SERVER's refusal is a hard field error. The local hint renders below the field.
    else if (serverWorkEmailRefusal) e.email = t("Please use your work email address.");
    if (!form.countryId) e.countryId = t("Required");
    // 10 tracks IdentityOptions.Password.RequiredLength (S-01(b), Program.cs). Below it the
    // server rejects the signup outright, so validating at 8 here only delays the same refusal.
    if (!form.password || form.password.length < 10) e.password = t("Password is too short.");
    if (!accepted) e.terms = t("Please accept the Terms of Service to continue.");
    return e;
  }, [form, accepted, t, activeRefusal, serverWorkEmailRefusal]);

  const isValid = Object.keys(fieldErrors).length === 0;

  const progress = useMemo(() => {
    const filled = [
      form.companyName.trim().length > 0,
      form.firstName.trim().length > 0,
      form.lastName.trim().length > 0,
      validateEmail(form.email) && isLikelyWorkEmail(form.email),
      !!form.countryId,
      form.password.length >= 10,
      accepted,
    ].filter(Boolean).length;
    return Math.round((filled / 7) * 100);
  }, [form, accepted]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  /**
   * CO-01. Turns a work-email rejection into an inline field error plus the contact-us escape hatch,
   * rather than the generic red banner, so the visitor is told what to change and where to go if
   * they cannot. Returns true when the error was one of ours and has been handled.
   */
  const applyEmailRefusal = (err: ApiError | undefined): boolean => {
    const code = err?.code as EmailRefusal["code"] | undefined;
    if (code !== "WORK_EMAIL_REQUIRED" && code !== "INVALID_EMAIL") return false;
    const details = (err as unknown as { details?: { contactUsUrl?: string } } | undefined)?.details;
    setRefusal({
      email: form.email.trim().toLowerCase(),
      code,
      contactUrl: details?.contactUsUrl || SIGNUP_CONTACT_URL,
    });
    return true;
  };

  const submit = async () => {
    setTouched(true);
    setServerError(null);
    setRefusal(null);
    if (!isValid) return;
    setSubmitting(true);
    try {
      const res = await signupFree({
        company: {
          name: form.companyName.trim(),
          countryId: Number(form.countryId),
          phoneNumber: form.phoneNumber.trim(),
          timeZone,
        },
        user: {
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim(),
          phoneNumber: form.phoneNumber.trim(),
          password: form.password,
          countryId: Number(form.countryId),
        },
        acceptedTerms: true,
        referenceSlug,
      });
      if (res.errors && res.errors.length > 0) {
        const err: ApiError = res.errors[0];
        if (applyEmailRefusal(err)) {
          setSubmitting(false);
          return;
        }
        // CO-359 — a password can fail up to THREE Identity rules at once and the server returns
        // every one of them, already localized by LocalizedIdentityErrorDescriber. Reading only
        // `errors[0]` showed one, the visitor fixed it, and got refused again on the next. The
        // shared helper joins all of them (newline-separated; rendered with whitespace-pre-line).
        setServerError(
          getApiErrorMessage(res, t, t("Something went wrong. Please try again.")),
        );
        setSubmitting(false);
        return;
      }
      track(AnalyticsEvents.SignupFreeSubmitted, {
        has_reference: !!referenceSlug,
        reference: referenceSlug || null,
      });
      setSubmitted(true);
    } catch (e: unknown) {
      const apiErr =
        (e as { errors?: ApiError[] })?.errors?.[0] ||
        (e as { response?: { data?: { errors?: ApiError[] } } })?.response?.data?.errors?.[0];
      // A 400 can surface as a thrown rejection rather than an errors payload, so the work-email
      // refusal has to be recognised on both paths.
      if (!applyEmailRefusal(apiErr)) {
        // Same full-list treatment on the thrown-rejection path: a 400 from the password
        // pre-flight arrives here, not in the resolved branch.
        setServerError(getApiErrorMessage(e, t, t("Something went wrong. Please try again.")));
      }
      setSubmitting(false);
    }
  };

  /* ─────────────────────── Success state ─────────────────────── */
  if (submitted) {
    return (
      <div className="min-h-screen relative flex items-center justify-center bg-[#F5F8FC] px-4 py-10 overflow-hidden">
        <BrandMesh />
        <motion.div
          initial={{ opacity: 0, y: 8, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.5, ease: EASE }}
          className="relative z-10 max-w-md w-full text-center"
        >
          <img src={CircleCheck} alt="" className="mx-auto h-20 w-20 mb-5" />
          <h1 className="text-2xl font-semibold text-foreground tracking-tight">
            {t("Welcome to Mowazaf.")}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {t("Your workspace is being set up. We've emailed you the next steps.")}
          </p>
          <div className="mt-6 flex items-center justify-center gap-3">
            <Button onClick={goToLogin}>
              {t("Continue to sign in")}
              <ArrowRight size={16} className="ms-2 rtl:rotate-180" />
            </Button>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            {t("Redirecting in a few seconds...")}
          </p>
        </motion.div>
      </div>
    );
  }

  /* ─────────────────────── Form state ─────────────────────── */

  const headlineParent: Variants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: reduceMotion ? 0 : 0.09,
        delayChildren: reduceMotion ? 0 : 0.18,
      },
    },
  };
  const lineVariants: Variants = {
    hidden: { opacity: 0, y: reduceMotion ? 0 : 8 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.52, ease: EASE } },
  };
  const featureParent: Variants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: reduceMotion ? 0 : 0.08,
        delayChildren: reduceMotion ? 0 : 0.5,
      },
    },
  };
  const featureItem: Variants = {
    hidden: { opacity: 0, y: reduceMotion ? 0 : 6 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.42, ease: EASE } },
  };
  const cardVariants: Variants = {
    hidden: { opacity: 0, x: reduceMotion ? 0 : 12 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.56, delay: reduceMotion ? 0 : 0.22, ease: EASE } },
  };
  const tailVariants: Variants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.6, delay: reduceMotion ? 0 : 0.85, ease: EASE } },
  };

  return (
    <div className="min-h-screen relative overflow-x-hidden bg-[#F5F8FC] text-foreground">
      <BrandMesh />

      <TermsDialog
        open={termsOpen}
        onOpenChange={setTermsOpen}
        kind="GeneralUsage"
        autoAccept={false}
        onAccepted={() => setAccepted(true)}
      />

      <div className="relative z-10 grid grid-cols-1 md:grid-cols-[44%_56%] lg:grid-cols-[40%_60%] min-h-screen">
        {/* ─── Left brand pane (desktop only) ─── */}
        <aside
          aria-hidden="true"
          className="hidden md:grid grid-rows-[auto_1fr_auto] p-10 lg:p-14 relative"
        >
          {/* Row 1 — brand block */}
          <motion.header
            initial={{ opacity: 0, y: reduceMotion ? 0 : 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.48, ease: EASE }}
            className="flex items-center gap-3"
          >
            <a
              href="/"
              className="group inline-flex items-center gap-3"
              aria-label="Mowazaf"
            >
              <img
                src={Logo}
                alt=""
                className="h-9 lg:h-10 w-auto select-none transition-transform duration-300 ease-out group-hover:scale-[1.04]"
              />
              <span className="text-lg lg:text-xl font-semibold tracking-tight text-foreground">
                {t("Mowazaf")}
              </span>
            </a>
          </motion.header>

          {/* Row 2 — headline + sub-copy + features (vertically centered) */}
          <div className="self-center max-w-[480px] py-12">
            <motion.h1
              variants={headlineParent}
              initial="hidden"
              animate="visible"
              className="text-4xl lg:text-5xl xl:text-[3.4rem] font-semibold tracking-tight leading-[1.05] text-foreground"
            >
              <motion.span variants={lineVariants} className="block">
                {t("Start free.")}
              </motion.span>
              <motion.span variants={lineVariants} className="block text-foreground/75">
                {t("Upgrade when you're ready.")}
              </motion.span>
            </motion.h1>

            <motion.p
              variants={lineVariants}
              initial="hidden"
              animate="visible"
              transition={{ duration: 0.5, delay: reduceMotion ? 0 : 0.42, ease: EASE }}
              className="mt-5 text-sm lg:text-base leading-relaxed text-muted-foreground max-w-[44ch]"
            >
              {t("Bring your team into a calm, modern HR workspace. No credit card to begin, no time pressure to upgrade.")}
            </motion.p>

            <motion.ul
              variants={featureParent}
              initial="hidden"
              animate="visible"
              className="mt-9 space-y-4"
            >
              <FeatureLine
                variants={featureItem}
                icon={<Users size={16} />}
                title={t("Built for the whole team")}
                body={t("Employees, managers, and HR working from one place.")}
              />
              <FeatureLine
                variants={featureItem}
                icon={<Layers size={16} />}
                title={t("Every module included")}
                body={t("Attendance, leaves, payroll, performance. Toggle what you need.")}
              />
              <FeatureLine
                variants={featureItem}
                icon={<ShieldCheck size={16} />}
                title={t("Secure by default")}
                body={t("Encrypted in transit and at rest, with role-based access so the right people see the right things.")}
              />
            </motion.ul>
          </div>

          {/* Row 3 — social proof */}
          <motion.div
            variants={tailVariants}
            initial="hidden"
            animate="visible"
          >
            <p className="text-xs text-muted-foreground/85 leading-snug max-w-[28ch]">
              {t("Trusted by HR teams all over the Region.")}
            </p>
          </motion.div>
        </aside>

        {/* ─── Right form pane ─── */}
        <main className="relative bg-white/80 md:bg-white/85 flex items-center justify-center p-4 sm:p-6 md:p-10">
          {/* Subtle inner left edge fade so the brand mesh doesn't pile up on the seam */}
          <div
            aria-hidden
            className="hidden md:block absolute top-0 bottom-0 ltr:left-0 rtl:right-0 w-12
                       bg-gradient-to-r rtl:bg-gradient-to-l from-white/0 via-white/40 to-white/70
                       pointer-events-none"
          />

          <motion.div
            variants={cardVariants}
            initial="hidden"
            animate="visible"
            className="relative w-full max-w-[480px]"
          >
            {/* Mobile-only brand block */}
            <div className="md:hidden mb-6 text-center">
              <a
                href="/"
                className="inline-flex items-center justify-center gap-2.5"
                aria-label="Mowazaf"
              >
                <img src={Logo} alt="" className="h-9 w-auto select-none" />
                <span className="text-lg font-semibold tracking-tight text-foreground">
                  {t("Mowazaf")}
                </span>
              </a>
              <h1 className="mt-4 text-xl font-semibold tracking-tight text-foreground leading-tight">
                {t("Start free.")}{" "}
                <span className="text-muted-foreground font-normal">
                  {t("Upgrade when you're ready.")}
                </span>
              </h1>
              <div className="mt-2 text-xs text-muted-foreground">
                <a
                  href={LOGIN_URL}
                  className="hover:text-foreground transition-colors underline-offset-2 hover:underline"
                >
                  {t("Already have an account?")}
                </a>
              </div>
            </div>

            {/* Gradient-bordered card. Outer holds the gradient; inner holds
                the surface — 1 px gap creates the border. */}
            <div
              className="rounded-2xl p-px shadow-[0_10px_40px_-15px_rgba(48,78,161,0.25)]"
              style={{
                backgroundImage:
                  "linear-gradient(180deg, rgba(48,78,161,0.45) 0%, rgba(74,221,203,0.18) 35%, var(--color-border) 100%)",
              }}
            >
              <div className="mz-form-card rounded-[15px] bg-card overflow-hidden">
                {/* Progress affordance */}
                <div
                  className="h-[3px] bg-primary/10 relative"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={progress}
                  aria-label={t("Form completion")}
                >
                  <div
                    className={cn(
                      "absolute inset-y-0 start-0 transition-[width] duration-500 ease-out",
                      progress > 0 && progress < 100 && "mz-progress-fill",
                      progress === 100 && "mz-progress-fill is-complete",
                      progress === 0 && "bg-primary",
                    )}
                    style={{ width: `${progress}%` }}
                    aria-hidden
                  />
                </div>

                <div className="px-6 pt-6 pb-2">
                  <div className="hidden md:flex items-center justify-between mb-2">
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium bg-primary/10 text-primary">
                      <Sparkles size={12} className="mz-twinkle" />
                      {t("Free plan")}
                    </span>
                    <a
                      href={LOGIN_URL}
                      className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {t("Already have an account?")}
                    </a>
                  </div>
                  <h2 className="text-xl font-semibold tracking-tight text-foreground">
                    {t("Create your workspace")}
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {t("Takes under a minute.")}
                  </p>
                </div>

                <div className="px-6 pb-6 pt-4 space-y-4">
                  <Field
                    label={t("Company name")}
                    required
                    error={touched ? fieldErrors.companyName : undefined}
                  >
                    <Input
                      value={form.companyName}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        update("companyName", e.target.value)
                      }
                      placeholder={t("Acme Inc.")}
                      autoComplete="organization"
                    />
                  </Field>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field
                      label={t("First name")}
                      required
                      error={touched ? fieldErrors.firstName : undefined}
                    >
                      <Input
                        value={form.firstName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          update("firstName", e.target.value)
                        }
                        autoComplete="given-name"
                      />
                    </Field>
                    <Field
                      label={t("Last name")}
                      required
                      error={touched ? fieldErrors.lastName : undefined}
                    >
                      <Input
                        value={form.lastName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          update("lastName", e.target.value)
                        }
                        autoComplete="family-name"
                      />
                    </Field>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <Field
                      label={t("Work email")}
                      required
                      error={touched ? fieldErrors.email : undefined}
                    >
                      <div className="relative">
                        <Lock
                          size={14}
                          className="absolute top-1/2 -translate-y-1/2 ltr:left-3 rtl:right-3 text-muted-foreground pointer-events-none"
                          aria-hidden
                        />
                        <Input
                          type="email"
                          value={form.email}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            update("email", e.target.value)
                          }
                          placeholder="you@company.com"
                          autoComplete="email"
                          className="ltr:pl-9 rtl:pr-9"
                        />
                      </div>
                    </Field>

                    {/* CO-01 escape hatch. Sole traders across the GCC really do run on Gmail, so a
                        refusal must never be a dead end. The whole sentence is the link: one
                        translation key, no interpolation, and it mirrors cleanly under RTL. */}
                    {touched && emailNeedsWorkAddress && (
                      <a
                        href={activeRefusal?.contactUrl || SIGNUP_CONTACT_URL}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-start gap-1 text-xs text-primary underline underline-offset-2 hover:opacity-80"
                      >
                        <span>{t("Using a personal address? Contact us and we'll set you up.")}</span>
                        <ArrowUpRight
                          size={12}
                          className="mt-0.5 shrink-0 rtl:-scale-x-100"
                          aria-hidden
                        />
                      </a>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field
                      label={t("Country")}
                      required
                      error={touched ? fieldErrors.countryId : undefined}
                    >
                      <Select
                        searchable
                        value={form.countryId || ""}
                        options={countries.map((c) => ({ value: c.id, label: c.name }))}
                        onChange={(val: number) => update("countryId", val)}
                        placeholder={t("Select a country")}
                      />
                    </Field>
                    <Field label={t("Phone (optional)")}>
                      <Input
                        type="tel"
                        value={form.phoneNumber}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          update("phoneNumber", e.target.value)
                        }
                        autoComplete="tel"
                        placeholder="+971…"
                      />
                    </Field>
                  </div>

                  <Field
                    label={t("Password")}
                    required
                    error={touched ? fieldErrors.password : undefined}
                    hint={t("We generated a strong one for you. Save it somewhere safe.")}
                  >
                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        value={form.password}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          update("password", e.target.value)
                        }
                        className="font-mono ltr:pr-20 rtl:pl-20 tracking-wide"
                        autoComplete="new-password"
                      />
                      <div className="absolute top-1/2 -translate-y-1/2 ltr:right-1 rtl:left-1 flex items-center">
                        <IconBtn
                          onClick={() => setShowPassword((s) => !s)}
                          label={showPassword ? t("Hide password") : t("Show password")}
                        >
                          {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                        </IconBtn>
                        <IconBtn
                          onClick={() => update("password", generatePassword())}
                          label={t("Regenerate password")}
                        >
                          <RefreshCw size={14} />
                        </IconBtn>
                      </div>
                    </div>
                  </Field>

                  <div className="pt-1">
                    <label className="flex items-start gap-2.5 cursor-pointer group">
                      <Checkbox
                        checked={accepted}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setAccepted(e.target.checked)
                        }
                      />
                      <span className="text-xs leading-relaxed text-muted-foreground -mt-0.5">
                        {t("I have read and accept the Mowazaf ")}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            setTermsOpen(true);
                          }}
                          className="text-primary underline underline-offset-2 hover:opacity-80"
                        >
                          {t("Terms of Service")}
                        </button>
                        .
                      </span>
                    </label>
                    {touched && fieldErrors.terms && (
                      <p className="mt-1.5 text-xs text-destructive ps-7">{fieldErrors.terms}</p>
                    )}
                  </div>

                  {/* CO-01. The server refused the submit, which means the client-side hint missed
                      it (a direct POST, or the runtime list moved ahead of this bundle). Say what
                      happened at submit level; the field above says what to change. */}
                  {activeRefusal && (
                    <div
                      role="alert"
                      className="rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 p-3 text-sm text-red-800 dark:text-red-300"
                    >
                      {t("We couldn't accept that address")}
                    </div>
                  )}

                  {serverError && (
                    <div
                      role="alert"
                      className="rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 p-3 text-sm text-red-800 dark:text-red-300 whitespace-pre-line"
                    >
                      {serverError}
                    </div>
                  )}

                  <Button
                    onClick={submit}
                    loading={submitting}
                    disabled={!isValid && touched}
                    fullWidth
                    size="lg"
                    className="mz-cta mt-1"
                  >
                    {t("Create my workspace")}
                    <ArrowRight size={16} className="ms-2 rtl:rotate-180" />
                  </Button>

                  <p className="text-[11px] text-center text-muted-foreground">
                    <CheckCircle2
                      size={11}
                      className="inline-block me-1 -mt-0.5 text-emerald-600 dark:text-emerald-400"
                    />
                    {t("Free forever. No card. Upgrade to a paid plan when you're ready.")}
                  </p>
                </div>
              </div>
            </div>

            <p className="mt-5 text-center text-xs text-muted-foreground">
              {t("By creating a workspace you agree to our terms and acknowledge our privacy practices.")}
            </p>
          </motion.div>
        </main>
      </div>
    </div>
  );
};

/* ─────────────────────────── Subcomponents ─────────────────────────── */

/** Layered radial-gradient mesh + grain. Fills the parent's `absolute inset-0`.
 *  Two ambient drift layers animate via CSS keyframes (see index.css). The
 *  cobalt + turquoise centres sit inside the left brand pane on desktop; the
 *  form pane's bg-white wash mutes whatever leaks through on the right. */
/**
 * CO-359 — decorative cobalt/turquoise mesh aimed under the brand pane.
 *
 * 🔴 Every focus below is positioned between 6% and 32% on X, i.e. under grid column 1. That is the
 * brand pane in LTR. Under RTL the grid flips the pane to the physical right, but radial-gradient
 * position percentages resolve against the painted box and do NOT flip — so in Arabic the whole
 * treatment sat behind the form pane and the brand panel rendered flat white. `.mz-mesh-root` carries
 * a `[dir="rtl"] { transform: scaleX(-1) }` rule (src/index.css) so the layer mirrors with the pane.
 * Keep the class on this wrapper if the gradient stops are ever retuned.
 */
const BrandMesh: React.FC = () => (
  <div aria-hidden className="mz-mesh-root pointer-events-none absolute inset-0 overflow-hidden">
    {/* Cobalt focus — top-left of brand pane */}
    <div
      className="absolute inset-0 mz-mesh-a"
      style={{
        background:
          "radial-gradient(62% 55% at 22% 26%, rgba(18,58,167,0.55) 0%, rgba(18,58,167,0) 65%)",
      }}
    />
    {/* Turquoise focus — bottom of brand pane */}
    <div
      className="absolute inset-0 mz-mesh-b"
      style={{
        background:
          "radial-gradient(50% 42% at 32% 78%, rgba(74,221,203,0.50) 0%, rgba(74,221,203,0) 70%)",
      }}
    />
    {/* Soft linen warmth — centre, ties the mesh together */}
    <div
      className="absolute inset-0"
      style={{
        background:
          "radial-gradient(70% 55% at 18% 50%, rgba(246,239,234,0.55) 0%, rgba(246,239,234,0) 75%)",
      }}
    />
    {/* Far-edge cobalt glow — leaks slightly into the form pane seam */}
    <div
      className="absolute inset-0"
      style={{
        background:
          "radial-gradient(40% 35% at 6% 90%, rgba(48,78,161,0.32) 0%, rgba(48,78,161,0) 70%)",
      }}
    />
    <div className="absolute inset-0 mz-grain" />
  </div>
);

interface FieldProps {
  label: string;
  required?: boolean;
  error?: string | null;
  hint?: string;
  children: React.ReactNode;
}
const Field: React.FC<FieldProps> = ({ label, required, error, hint, children }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-xs font-medium text-foreground/90">
      {label}
      {required && <span className="text-destructive ms-0.5">*</span>}
    </label>
    <div className="mz-field">{children}</div>
    {error ? (
      <p className="text-xs text-destructive">{error}</p>
    ) : hint ? (
      <p className="text-xs text-muted-foreground/80">{hint}</p>
    ) : null}
  </div>
);

interface FeatureLineProps {
  icon: React.ReactNode;
  title: string;
  body: string;
  variants?: Variants;
}
const FeatureLine: React.FC<FeatureLineProps> = ({ icon, title, body, variants }) => (
  <motion.li variants={variants} className="flex items-start gap-3">
    <span className="shrink-0 w-7 h-7 rounded-md bg-white border border-border text-primary flex items-center justify-center mt-0.5 shadow-[0_1px_2px_rgba(48,78,161,0.06)]">
      {icon}
    </span>
    <div className="min-w-0">
      <div className="text-sm font-medium text-foreground">{title}</div>
      <div className="text-xs text-muted-foreground mt-0.5 max-w-[44ch]">{body}</div>
    </div>
  </motion.li>
);

interface IconBtnProps {
  onClick: () => void;
  label: string;
  children: React.ReactNode;
}
const IconBtn: React.FC<IconBtnProps> = ({ onClick, label, children }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    title={label}
    className={cn(
      "inline-flex items-center justify-center w-7 h-7 rounded-md",
      "text-muted-foreground hover:bg-muted hover:text-foreground transition-colors",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30",
      "active:scale-95",
    )}
  >
    {children}
  </button>
);

export default SignupFree;
